<script setup lang="ts" generic="T extends string | number | boolean">
    interface FormCheckboxProps {
        forId: string;
        trueValue?: T;
        falseValue?: T;
        label: string;
        title?: string;
        description?: string;
        caption?: string;
        required?: boolean;
        hidden?: boolean;
        error?: string;
        checkedValue?: T;
        name?: string | number;
        disabled?: boolean;
        // TODO: discuss if noMargin should be default
        // I think margin is not part of the component, but is the job of the layout that uses the component.
        // https://onewoom.atlassian.net/browse/ECOMM-4957
        noMargin?: boolean;
    }

    const props = withDefaults(defineProps<FormCheckboxProps>(), {
        required: false,
        trueValue: () => 'yes' as T,
        falseValue: () => 'no' as T,
        title: '',
        description: '',
        error: '',
        name: '',
    });

    const emit = defineEmits(['update:checkedValue', 'update:consent']);

    const toggle = computed({
        get: () => {
            return props.checkedValue;
        },
        set: (newValue) => {
            emit('update:checkedValue', newValue);
            emit('update:consent', { name: props.name, value: newValue });
        },
    });
</script>

<template>
    <div :class="['mol-form-checkbox relative', { 'text-woom-red': !!error, hidden: hidden, 'mb-5': !noMargin }]">
        <div class="flex flex-col gap-3">
            <p
                v-if="title"
                class="link-style">
                <renderer-html :content="woomTextFormat(title)" />
            </p>
            <p
                v-if="description"
                class="link-style">
                <renderer-html :content="woomTextFormat(description)" />
            </p>
            <div class="relative ml-1.5 flex">
                <input
                    v-model="toggle"
                    :aria-describedby="error ? `${forId}-help` : undefined"
                    :class="[
                        'peer relative size-4.5 shrink-0 appearance-none rounded-xs border-2 border-woom-black bg-transparent accent-woom-black ring-offset-2 outline-hidden transition-all before:absolute before:top-1/2 before:left-1/2 before:block before:size-10 before:-translate-x-1/2 before:-translate-y-1/2 before:rounded-full before:bg-transparent before:opacity-0 before:transition-opacity checked:bg-woom-black hover:before:bg-woom-black hover:before:opacity-10 focus:ring-2 focus:ring-woom-black checked:focus:ring-transparent checked:focus:before:bg-woom-black checked:focus:before:opacity-[.12] active:focus:ring-transparent disabled:opacity-30 disabled:before:opacity-0',
                    ]"
                    :disabled="disabled"
                    :id="forId"
                    :false-value="falseValue"
                    :name="forId"
                    :required="required"
                    :true-value="trueValue"
                    :value="trueValue"
                    type="checkbox" />

                <div class="ml-4 flex -translate-y-0.5 flex-col gap-1">
                    <span>
                        <label
                            :for="forId"
                            class="link-style">
                            <renderer-html :content="woomTextFormat(label)" />
                        </label>
                        <lazy-atm-form-required v-if="required" />
                    </span>

                    <span
                        v-if="caption"
                        class="text-xs text-woom-grey-moon [&_a]:underline">
                        <renderer-html :content="caption" />
                    </span>
                </div>

                <!-- TODO: Replace polyline with actual SVG icon from woom CDN -->
                <!-- https://onewoom.atlassian.net/browse/ECOMM-4959 -->
                <svg
                    aria-hidden="true"
                    class="pointer-events-none absolute hidden size-4.5 stroke-white stroke-3 peer-checked:block max-md:top-0"
                    fill="none"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg">
                    <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
            </div>
        </div>
        <lazy-atm-form-error
            v-if="error"
            :error="error"
            :for-id="forId" />
    </div>
</template>
